import React, { useState } from 'react';
import axios from 'axios';
import { MessageCircle, Mail, MessageSquare, Send, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportChatSettings = ({ config, token, onChange }) => {
  const [showKey, setShowKey] = useState(false);
  const [keyDraft, setKeyDraft] = useState('');
  const [showCmbKey, setShowCmbKey] = useState(false);
  const [cmbKeyDraft, setCmbKeyDraft] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, message}
  const [testingWA, setTestingWA] = useState(false);
  const [testResultWA, setTestResultWA] = useState(null);

  // Local mirror of text fields so we don't fire onChange on every keystroke
  // (the parent persists each call to the backend). Sync from props on mount
  // and whenever the config changes externally.
  const [emailLocal, setEmailLocal] = useState('');
  const [whatsappLocal, setWhatsappLocal] = useState('');
  const [welcomeLocal, setWelcomeLocal] = useState('');
  const [senderLocal, setSenderLocal] = useState('');
  const [timeoutLocal, setTimeoutLocal] = useState(30);

  React.useEffect(() => {
    if (!config) return;
    setEmailLocal(config.support_email || '');
    setWhatsappLocal(config.support_whatsapp || '');
    setWelcomeLocal(config.support_welcome_message || '');
    setSenderLocal(config.support_sender_email || 'onboarding@resend.dev');
    setTimeoutLocal(config.support_unanswered_minutes || 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.support_email, config?.support_whatsapp, config?.support_welcome_message, config?.support_sender_email, config?.support_unanswered_minutes]);

  if (!config) return null;

  const enabled = !!config.support_chat_enabled;
  const senderEmail = senderLocal;
  const welcome = welcomeLocal;
  const timeoutMin = timeoutLocal;
  const keySet = !!config.support_resend_api_key_set;
  const keyPreview = config.support_resend_api_key_preview || '';
  const cmbKeySet = !!config.support_callmebot_apikey_set;
  const cmbKeyPreview = config.support_callmebot_apikey_preview || '';

  const handleSaveKey = async () => {
    if (!keyDraft.trim()) return;
    await onChange({ support_resend_api_key: keyDraft.trim() });
    setKeyDraft('');
    setShowKey(false);
  };

  const handleClearKey = async () => {
    if (!confirm('Resend API açarını silmək istəyirsiniz?')) return;
    await onChange({ support_resend_api_key: '' });
  };

  const handleSaveCmbKey = async () => {
    if (!cmbKeyDraft.trim()) return;
    await onChange({ support_callmebot_apikey: cmbKeyDraft.trim() });
    setCmbKeyDraft('');
    setShowCmbKey(false);
  };

  const handleClearCmbKey = async () => {
    if (!confirm('CallMeBot API açarını silmək istəyirsiniz?')) return;
    await onChange({ support_callmebot_apikey: '' });
  };

  const handleTestWhatsApp = async () => {
    setTestingWA(true);
    setTestResultWA(null);
    try {
      await axios.post(`${API}/admin/support/test-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestResultWA({ ok: true, message: 'WhatsApp-a test mesaj göndərildi!' });
    } catch (e) {
      setTestResultWA({ ok: false, message: e.response?.data?.detail || 'Xəta baş verdi' });
    } finally {
      setTestingWA(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await axios.post(`${API}/admin/support/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestResult({ ok: true, message: 'Test email göndərildi! Inbox-ı yoxlayın.' });
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || 'Xəta baş verdi' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid="support-chat-settings">
      <h3 className="admin-section-title flex items-center gap-2">
        <MessageCircle size={16} /> Dəstək çatı
      </h3>
      <p className="admin-section-desc">
        Ziyarətçilər saytın sağ alt küncündəki bənövşəyi dəstək çat düyməsindən sizinlə əlaqə saxlaya bilərlər.
        Mesajlar admin paneldə görünür və {timeoutMin} dəq cavabsız qalarsa avtomatik email bildirişi göndərilir.
      </p>

      {/* Enable toggle */}
      <div
        style={{
          background: enabled ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${enabled ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: 10,
          padding: '14px 16px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Dəstək çatı aktiv</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
            Söndürülsə, ziyarətçilər bənövşəyi düyməni görməyəcəklər
          </div>
        </div>
        <label className="admin-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onChange({ support_chat_enabled: e.target.checked })}
            data-testid="support-enabled-toggle"
          />
          <span className="admin-toggle-slider" />
        </label>
      </div>

      {/* Welcome message */}
      <label className="admin-label" style={{ marginTop: 4 }}>
        Salamlama mesajı (chat açılan kimi göstərilir)
      </label>
      <textarea
        className="admin-textarea"
        value={welcome}
        onChange={(e) => setWelcomeLocal(e.target.value)}
        onBlur={() => onChange({ support_welcome_message: welcomeLocal })}
        maxLength={500}
        rows={2}
        placeholder="Salam! Sualınızı yazın, ən qısa zamanda cavablayacağıq."
        data-testid="support-welcome-input"
      />

      {/* Contact info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 16 }}>
        <div>
          <label className="admin-label flex items-center gap-2">
            <Mail size={14} /> Bildiriş email
          </label>
          <input
            type="email"
            className="karma-input"
            style={{ width: '100%', textAlign: 'left' }}
            value={emailLocal}
            onChange={(e) => setEmailLocal(e.target.value)}
            onBlur={() => onChange({ support_email: emailLocal })}
            placeholder="example@gmail.com"
            data-testid="support-email-input"
          />
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Cavabsız mesajlar bura göndəriləcək
          </div>
        </div>

        <div>
          <label className="admin-label flex items-center gap-2">
            <MessageSquare size={14} /> Sizin WhatsApp nömrəniz
          </label>
          <input
            type="tel"
            className="karma-input"
            style={{ width: '100%', textAlign: 'left' }}
            value={whatsappLocal}
            onChange={(e) => setWhatsappLocal(e.target.value)}
            onBlur={() => onChange({ support_whatsapp: whatsappLocal })}
            placeholder="+994 50 ..."
            data-testid="support-whatsapp-input"
          />
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Email-də "WhatsApp-da cavabla" düyməsi üçün (istifadəçi öz nömrəsini paylaşmasa)
          </div>
        </div>
      </div>

      {/* Timeout + sender */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 14 }}>
        <div>
          <label className="admin-label">Cavabsız müddəti (dəq)</label>
          <input
            type="number"
            className="karma-input"
            style={{ width: '100%', textAlign: 'left' }}
            min={1}
            max={1440}
            value={timeoutLocal}
            onChange={(e) => setTimeoutLocal(parseInt(e.target.value || '30', 10))}
            onBlur={() => onChange({ support_unanswered_minutes: timeoutLocal })}
            data-testid="support-timeout-input"
          />
        </div>
        <div>
          <label className="admin-label">Göndərən email (sender)</label>
          <input
            type="email"
            className="karma-input"
            style={{ width: '100%', textAlign: 'left' }}
            value={senderLocal}
            onChange={(e) => setSenderLocal(e.target.value)}
            onBlur={() => onChange({ support_sender_email: senderLocal })}
            placeholder="onboarding@resend.dev"
            data-testid="support-sender-input"
          />
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Resend-də verified domain-iniz varsa istifadə edin
          </div>
        </div>
      </div>

      {/* Resend API key */}
      <div
        style={{
          marginTop: 22,
          padding: 16,
          background: 'rgba(168,85,247,0.04)',
          border: '1px solid rgba(168,85,247,0.18)',
          borderRadius: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f0e68c' }}>Resend API açarı</div>
          {keySet && (
            <span
              style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 10,
                background: 'rgba(34,197,94,0.15)',
                color: '#86efac',
                border: '1px solid rgba(34,197,94,0.3)',
              }}
            >
              ✓ Quraşdırılıb
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>
          <a
            href="https://resend.com/api-keys"
            target="_blank"
            rel="noreferrer"
            style={{ color: '#c4b5fd', textDecoration: 'underline' }}
          >
            resend.com/api-keys
          </a>
          {' '}— pulsuz hesab açın, "Create API Key" düyməsinə basın, açarı kopyalayıb buraya yapışdırın.
        </div>

        {keySet && !showKey && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <code
              style={{
                fontFamily: 'monospace',
                background: 'rgba(0,0,0,0.4)',
                padding: '6px 10px',
                borderRadius: 6,
                color: '#c4b5fd',
                fontSize: 12,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              data-testid="support-key-preview"
            >
              {keyPreview}
            </code>
            <button type="button" onClick={() => setShowKey(true)} className="admin-btn-secondary" data-testid="support-key-edit-btn">
              Dəyiş
            </button>
            <button type="button" onClick={handleClearKey} className="admin-btn-secondary" style={{ color: '#fca5a5' }}>
              Sil
            </button>
          </div>
        )}

        {(!keySet || showKey) && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              className="karma-input"
              style={{ flex: 1, minWidth: 200, textAlign: 'left', fontFamily: 'monospace', fontSize: 12 }}
              placeholder="re_xxxxxxxxxxxxxxxx"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              data-testid="support-key-input"
            />
            <button
              type="button"
              onClick={handleSaveKey}
              disabled={!keyDraft.trim()}
              className="admin-btn-primary"
              data-testid="support-key-save-btn"
            >
              Saxla
            </button>
            {showKey && (
              <button type="button" onClick={() => { setShowKey(false); setKeyDraft(''); }} className="admin-btn-secondary">
                Ləğv
              </button>
            )}
          </div>
        )}

        {/* Test email button */}
        <button
          type="button"
          onClick={handleTestEmail}
          disabled={!keySet || !emailLocal || testing}
          className="admin-btn-primary"
          data-testid="support-test-email-btn"
          style={{ marginTop: 4 }}
        >
          <Send size={14} /> {testing ? 'Göndərilir...' : 'Test email göndər'}
        </button>
        {testResult && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 12px',
              background: testResult.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${testResult.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              borderRadius: 6,
              color: testResult.ok ? '#86efac' : '#fca5a5',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
            data-testid="support-test-result"
          >
            {testResult.ok ? <Check size={14} /> : <AlertCircle size={14} />}
            {testResult.message}
          </div>
        )}
      </div>

      {/* CallMeBot — pulsuz WhatsApp bildirişləri */}
      <div
        style={{
          marginTop: 22,
          padding: 16,
          background: 'rgba(37,211,102,0.05)',
          border: '1px solid rgba(37,211,102,0.25)',
          borderRadius: 10,
        }}
        data-testid="callmebot-section"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <MessageSquare size={16} style={{ color: '#25d366' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: '#86efac' }}>
            CallMeBot — pulsuz WhatsApp bildirişləri
          </div>
          {cmbKeySet && (
            <span
              style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 10,
                background: 'rgba(34,197,94,0.15)', color: '#86efac',
                border: '1px solid rgba(34,197,94,0.3)',
              }}
            >
              ✓ Quraşdırılıb
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 12, lineHeight: 1.6 }}>
          <strong style={{ color: '#fde68a' }}>Pulsuz WhatsApp bildirişləri</strong> — heç bir ödəniş, Twilio və ya WhatsApp Business hesabı lazım deyil.
          <br /><br />
          <strong>Quraşdırma (1 dəq):</strong>
          <ol style={{ marginTop: 6, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>WhatsApp-da bu nömrəni əlavə edin: <code style={{ background: 'rgba(0,0,0,0.4)', padding: '2px 6px', borderRadius: 4 }}>+34 644 51 95 23</code></li>
            <li>Həmin nömrəyə bu mesajı göndərin: <code style={{ background: 'rgba(0,0,0,0.4)', padding: '2px 6px', borderRadius: 4 }}>I allow callmebot to send me messages</code></li>
            <li>~1-2 dəq sonra bot sizə cavab göndərəcək, içində <strong>API key</strong> olacaq</li>
            <li>Həmin açarı aşağıya yapışdırın → Saxla</li>
          </ol>
          <div style={{ marginTop: 8, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
            ⓘ Yuxarıdakı <strong>"Sizin WhatsApp nömrəniz"</strong> sahəsində öz nömrənizin doğru olduğunu yoxlayın (məs. +994501234567).
            <br />
            ⓘ <a href="https://www.callmebot.com/blog/free-api-whatsapp-messages/" target="_blank" rel="noreferrer" style={{ color: '#c4b5fd' }}>Tam təlimat (ingiliscə)</a>
          </div>
        </div>

        {cmbKeySet && !showCmbKey && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <code
              style={{
                fontFamily: 'monospace', background: 'rgba(0,0,0,0.4)',
                padding: '6px 10px', borderRadius: 6, color: '#86efac',
                fontSize: 12, flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              data-testid="callmebot-key-preview"
            >
              {cmbKeyPreview}
            </code>
            <button type="button" onClick={() => setShowCmbKey(true)} className="admin-btn-secondary">
              Dəyiş
            </button>
            <button type="button" onClick={handleClearCmbKey} className="admin-btn-secondary" style={{ color: '#fca5a5' }}>
              Sil
            </button>
          </div>
        )}

        {(!cmbKeySet || showCmbKey) && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              className="karma-input"
              style={{ flex: 1, minWidth: 200, textAlign: 'left', fontFamily: 'monospace', fontSize: 12 }}
              placeholder="məs. 1234567"
              value={cmbKeyDraft}
              onChange={(e) => setCmbKeyDraft(e.target.value)}
              data-testid="callmebot-key-input"
            />
            <button
              type="button"
              onClick={handleSaveCmbKey}
              disabled={!cmbKeyDraft.trim()}
              className="admin-btn-primary"
              data-testid="callmebot-key-save-btn"
            >
              Saxla
            </button>
            {showCmbKey && (
              <button type="button" onClick={() => { setShowCmbKey(false); setCmbKeyDraft(''); }} className="admin-btn-secondary">
                Ləğv
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={handleTestWhatsApp}
          disabled={!cmbKeySet || !whatsappLocal || testingWA}
          className="admin-btn-primary"
          data-testid="callmebot-test-btn"
          style={{ marginTop: 4, background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)' }}
        >
          <MessageSquare size={14} /> {testingWA ? 'Göndərilir...' : 'WhatsApp-a test mesaj göndər'}
        </button>
        {testResultWA && (
          <div
            style={{
              marginTop: 10, padding: '8px 12px',
              background: testResultWA.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${testResultWA.ok ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
              borderRadius: 6,
              color: testResultWA.ok ? '#86efac' : '#fca5a5',
              fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
            }}
            data-testid="callmebot-test-result"
          >
            {testResultWA.ok ? <Check size={14} /> : <AlertCircle size={14} />}
            {testResultWA.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportChatSettings;
